import { useAuth } from '@/contexts/AuthContext';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useBetaCheckin } from '@/hooks/useBetaCheckin';
import { useBetaSigningGate } from '@/hooks/useBetaSigningGate';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, Lock, Info } from 'lucide-react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useState } from 'react';
import { BetaReadinessCheckinModal } from '@/components/beta/BetaReadinessCheckinModal';

export const BillingGate = ({ children }: { children: React.ReactNode }) => {
  const { activeShop } = useAuth();
  const { data: billingStatus, isLoading } = useBillingStatus(activeShop?.id);
  const { shouldShowCheckin, dayNumber, daysRemaining, betaStatus, shopId } = useBetaCheckin();
  const { needsSigning, needsLegalReview, isLoading: signingLoading } = useBetaSigningGate();
  // Internal admins administer the app — they must never be forced into
  // the repair-shop beta agreement / billing flows on their own dev
  // shops. Without this, clicking "View in dashboard" from the
  // New Beta Sign-Up admin email lands the admin on /beta-leads (a
  // valid internal-admin route) only to be redirected to
  // /beta/sign-agreement by `needsSigning` because the admin's dev
  // shop happens to have an unsigned beta agreement. Per-route
  // exemption would couple this gate to the InternalAdminRoute tree;
  // per-user exemption is cleaner and matches the product rule that
  // internal admin pages must bypass shop beta agreement onboarding.
  const { data: isInternalAdmin, isLoading: adminLoading } = useIsInternalAdmin();
  const [checkinOpen, setCheckinOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Routes that bypass billing gate to prevent redirect loops
  const publicPaths = ['/auth', '/auth/confirm', '/accept-invitation', '/onboard', '/beta', '/beta/complete', '/beta/sign-agreement', '/beta/legal-review'];
  const publicPrefixes = ['/billing', '/legal', '/join-team'];

  const isPublicPath = publicPaths.includes(location.pathname) ||
                       publicPrefixes.some(prefix => location.pathname.startsWith(prefix));

  if (isPublicPath || !activeShop?.id) {
    return <>{children}</>;
  }

  if (isLoading || signingLoading || adminLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // Internal-admin bypass — must run BEFORE the beta-signing /
  // legal-review redirects, otherwise an admin with an unsigned beta
  // dev shop loses access to /beta-leads, /admin/*, /admin/schools,
  // etc. InternalAdminRoute still enforces the 403 boundary on the
  // page itself for non-admin users, so making this user-scoped does
  // NOT widen access — it only avoids hijacking the navigation.
  if (isInternalAdmin) {
    return <>{children}</>;
  }

  // Beta signing gate — redirect if beta shop and agreement not yet signed
  if (needsSigning) {
    return <Navigate to="/beta/sign-agreement" replace />;
  }

  // Legal review gate — redirect if beta agreement signed but privacy/TOS/age missing
  if (needsLegalReview) {
    return <Navigate to="/beta/legal-review" replace />;
  }

  // Shared beta UX (check-in modal + milestone banner). The check-in
  // modal is manager-only by construction (useBetaCheckin gates
  // shouldShowCheckin / betaStatus on the manager role), so for a
  // technician / advisor this renders nothing — they get plain access.
  const betaContent = (
    <>
      {shouldShowCheckin && checkinOpen && shopId && (
        <BetaReadinessCheckinModal
          dayNumber={dayNumber}
          daysRemaining={daysRemaining}
          shopId={shopId}
          onClose={() => setCheckinOpen(false)}
        />
      )}
      {betaStatus === 'completed_awaiting_decision' && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>Beta Milestone Reached</AlertTitle>
          <AlertDescription>
            OptiBay is reviewing Go-Live readiness for your shop.
          </AlertDescription>
        </Alert>
      )}
    </>
  );

  // Internal or alpha shops - full access
  if (billingStatus?.billing_status === 'internal' || billingStatus?.billing_status === 'alpha') {
    return <>{children}</>;
  }

  // K28 BIL-1: active beta shops — full access for EVERY shop role. This
  // is driven by the server-authoritative get_shop_billing_status RPC
  // (billing_status === 'beta'), which returns the same answer for
  // owners, managers, technicians, and advisors. It replaces the prior
  // reliance on the manager-only useBetaCheckin betaStatus path, which
  // left non-manager staff read-only locked on a legitimate free beta
  // shop. No billing-management capability is granted here — portal /
  // checkout authorization is enforced in the stripe-* edge functions.
  if (billingStatus?.billing_status === 'beta') {
    return (
      <>
        {betaContent}
        {children}
      </>
    );
  }

  // Active or trialing subscription - full access
  if (billingStatus?.can_access_app) {
    // Show grace period warning if past_due
    if (billingStatus?.billing_status === 'past_due' && billingStatus?.is_in_grace_period) {
      return (
        <>
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Payment Issue</AlertTitle>
            <AlertDescription>
              Your subscription payment failed. You have {billingStatus.days_until_lockout} days remaining
              before your account becomes read-only.
              <Button
                variant="outline"
                size="sm"
                className="ml-4"
                onClick={() => navigate('/shop-profile?tab=billing')}
              >
                Update Payment Method
              </Button>
            </AlertDescription>
          </Alert>
          {children}
        </>
      );
    }

    return (
      <>
        {/* Beta check-in modal (non-blocking) */}
        {shouldShowCheckin && checkinOpen && shopId && (
          <BetaReadinessCheckinModal
            dayNumber={dayNumber}
            daysRemaining={daysRemaining}
            shopId={shopId}
            onClose={() => setCheckinOpen(false)}
          />
        )}

        {/* Day-60 milestone banner */}
        {betaStatus === 'completed_awaiting_decision' && (
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertTitle>Beta Milestone Reached</AlertTitle>
            <AlertDescription>
              OptiBay is reviewing Go-Live readiness for your shop.
            </AlertDescription>
          </Alert>
        )}

        {children}
      </>
    );
  }

  // Client-side fallback: if a beta shop's status didn't reach the
  // server RPC as 'beta' (e.g. the RPC migration hasn't been applied in
  // a given environment yet), still honor an active beta status read by
  // useBetaCheckin. This is a defense-in-depth backstop for managers;
  // the canonical, role-independent path is the billing_status === 'beta'
  // branch above.
  if (betaStatus && ['active', 'extended', 'completed_awaiting_decision', 'go_live_approved'].includes(betaStatus)) {
    return (
      <>
        {betaContent}
        {children}
      </>
    );
  }

  // Read-only mode - subscription required
  return (
    <div className="flex flex-col items-center justify-center h-screen p-8 text-center">
      {betaContent}
      <Lock className="h-16 w-16 text-destructive mb-4" />
      <h1 className="text-2xl font-bold mb-2">Subscription Required</h1>
      <p className="text-muted-foreground mb-6 max-w-md">
        Your subscription has expired or payment failed. Your shop is now in read-only mode.
        All your data is safe - update your billing to restore full access.
      </p>
      <Button onClick={() => navigate('/shop-profile?tab=billing')}>
        Manage Billing
      </Button>
    </div>
  );
};
